/*
  The Science of Light page (sections/science-of-light.liquid).
  - Red light / infrared tabs switch the spectrum band, caption and tissue drawing.
  - "What happens in the body" is a five-step story that plays through on its own until tapped.
  - Accordions marked data-sol-exclusive keep one item open at a time.
  - Research library chips filter the study cards.
  - Buy box thumbnails swap the main photo.
  - Load-in motion copies the homepage (fade up 24px, IntersectionObserver). Content is only
    hidden once this script adds .is-ready, so it stays visible without JavaScript.
*/
(function () {
  function init(root) {
    if (root.dataset.solInit) return;
    root.dataset.solInit = '1';

    // Tabs: each [data-sol-tabs] block drives the [data-sol-mode] figures inside it.
    root.querySelectorAll('[data-sol-tabs]').forEach(function (block) {
      var tabs = block.querySelectorAll('[data-sol-tab]');
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var mode = tab.dataset.solTab;
          tabs.forEach(function (t) {
            var on = t.dataset.solTab === mode;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          block.querySelectorAll('[data-sol-mode]').forEach(function (f) { f.dataset.mode = mode; });
          block.querySelectorAll('[data-sol-panel]').forEach(function (p) { p.hidden = p.dataset.solPanel !== mode; });
        });
      });
    });

    // Steps: one open at a time, and the matching callout lights up.
    root.querySelectorAll('[data-sol-steps]').forEach(function (card) {
      var items = card.querySelectorAll('details[data-step]');
      var callouts = card.querySelectorAll('.sol-callout');
      items.forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (!d.open) return;
          items.forEach(function (o) { if (o !== d) o.open = false; });
          callouts.forEach(function (c) { c.classList.toggle('is-active', c.dataset.step === d.dataset.step); });
        });
      });
    });

    root.querySelectorAll('[data-sol-exclusive]').forEach(function (group) {
      var items = group.querySelectorAll('details');
      items.forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (d.open) items.forEach(function (o) { if (o !== d) o.open = false; });
        });
      });
    });

    // "What happens in the body" story: five steps. Plays through on its own once it
    // scrolls into view; any tap, arrow or key stops the autoplay for good.
    root.querySelectorAll('[data-sol-story]').forEach(function (story) {
      var steps = [].slice.call(story.querySelectorAll('.sol-story__step'));
      var panels = story.querySelectorAll('.sol-story__panel');
      var callouts = story.querySelectorAll('.sol-callout');
      var fig = story.querySelector('.sol-story__fig');
      var num = story.querySelector('[data-sol-story-num]');
      var STEP_MS = 5500, timer = null, current = 0, stopped = false;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      story.style.setProperty('--sol-step', STEP_MS + 'ms');

      function show(i) {
        current = (i + steps.length) % steps.length;
        var st = steps[current], n = st.dataset.step;
        steps.forEach(function (b, k) { var on = k === current; b.classList.toggle('is-active', on); b.setAttribute('aria-selected', on ? 'true' : 'false'); });
        panels.forEach(function (pn) { pn.hidden = pn.dataset.step !== n; });
        callouts.forEach(function (c) { c.classList.toggle('is-active', c.dataset.step === n); });
        fig.style.setProperty('--sx', st.dataset.x);
        fig.style.setProperty('--sy', st.dataset.y);
        num.textContent = '0' + n;
        // restart the progress bar animation
        var bar = st.querySelector('.sol-story__bar i');
        if (bar) { bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = ''; }
      }
      function play() {
        if (stopped || reduce) return;
        story.classList.add('is-playing');
        clearInterval(timer);
        timer = setInterval(function () { show(current + 1); }, STEP_MS);
      }
      function stop() { stopped = true; clearInterval(timer); story.classList.remove('is-playing'); }

      steps.forEach(function (b, k) {
        b.addEventListener('click', function () { stop(); show(k); });
        b.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); stop(); show(k + 1); steps[current].focus(); }
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); stop(); show(k - 1); steps[current].focus(); }
        });
      });
      story.querySelector('[data-sol-story-prev]').addEventListener('click', function () { stop(); show(current - 1); });
      story.querySelector('[data-sol-story-next]').addEventListener('click', function () { stop(); show(current + 1); });
      show(0);

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) play();
            else if (!stopped) { clearInterval(timer); story.classList.remove('is-playing'); }
          });
        }, { threshold: 0.35 }).observe(story);
      }
    });

    // Research library filter
    root.querySelectorAll('[data-sol-library]').forEach(function (lib) {
      var cards = lib.querySelectorAll('.sol-study');
      lib.querySelectorAll('input[name="sol-topic"]').forEach(function (input) {
        input.addEventListener('change', function () {
          var v = input.value;
          cards.forEach(function (c) { c.hidden = !(v === 'all' || c.dataset.topic === v); });
        });
      });
    });

    // Gallery
    root.querySelectorAll('[data-sol-gallery]').forEach(function (g) {
      var main = g.querySelector('[data-sol-gallery-main]');
      var thumbs = g.querySelectorAll('.sol-gallery__thumb');
      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          main.src = t.dataset.src;
          thumbs.forEach(function (o) { o.classList.toggle('is-active', o === t); });
        });
      });
    });

    // Benefit tiles: numbers count up from zero when they scroll into view
    var counters = root.querySelectorAll('[data-sol-count]');
    var reduceCount = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (counters.length && 'IntersectionObserver' in window && !reduceCount) {
      var cio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          cio.unobserve(e.target);
          var el = e.target, end = parseInt(el.dataset.solCount, 10), t0 = null, DUR = 1400;
          (function tick(ts) {
            if (!t0) t0 = ts;
            var p = Math.min(1, (ts - t0) / DUR), eased = 1 - Math.pow(1 - p, 3);
            el.textContent = Math.round(end * eased);
            if (p < 1) requestAnimationFrame(tick);
          })(performance.now());
        });
      }, { threshold: 0.5 });
      counters.forEach(function (c) { c.textContent = '0'; cio.observe(c); });
    }

    // Load-in motion
    var targets = root.querySelectorAll('[data-sol-reveal], [data-sol-chart]');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!('IntersectionObserver' in window) || reduce) return;
    root.classList.add('is-ready');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -7.5% 0px', threshold: 0.12 });
    targets.forEach(function (t) {
      // Anything already on screen at load shows straight away.
      var r = t.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) t.classList.add('is-in');
      else io.observe(t);
    });
  }

  function boot() { document.querySelectorAll('[data-sol]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
